import React, { useCallback, useEffect, useState } from "react";
import { Col, Container, Offcanvas, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { connect, useDispatch } from "react-redux";
import { AvatarAction } from "../../redux/actions/AvatarAction";
import { AppConstant, AvatarConstant } from "../../redux/reducers";
import MButton from "../commons/MButton";
import { MBackspace, MDotActive, MDotEmpty, MDotFill, MDotWrong, MLeftButton } from "../commons/MIcon";

const NumberPanel = [
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5" },
  { value: "6", label: "6" },
  { value: "7", label: "7" },
  { value: "8", label: "8" },
  { value: "9", label: "9" },
  { value: "", label: "" },
  { value: "0", label: "0" },
  { value: -1, label: MBackspace, isComponent: true },
];

const AvatarPasswordSidebar = ({ show, ComparePasswordToServer, UpdateAvatarToUserConfirm }) => {
  const { t } = useTranslation();

  const [passwordStrList, setPasswordStrList] = useState((new Array(4)).fill(null));
  const [passwordStep, setPasswordStep] = useState(0);
  const [passwordStepDone, setPasswordStepDone] = useState(false);
  const [isMissMatching, setIsMissMatching] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!show) {
      setPasswordStrList((new Array(4)).fill(null));
      setPasswordStep(0);
      setPasswordStepDone(false);
    }
  }, [show]);

  useEffect(() => {
    setPasswordStepDone(passwordStrList.every(x => !!x));
  }, [passwordStrList]);

  const handlerClose = useCallback(() => {
    dispatch({ type: AppConstant.SET_SHOW_AVATAR_PASSWORD, show: false });
  }, [dispatch]);


  const setPassword = useCallback((val) => {
    if (passwordStrList.every(x => !!x) && val !== -1) {
      return;
    }
    if (val === -1) {
      if (passwordStep > 0) {
        setIsMissMatching(false);
        setPasswordStrList(prev => {
          const tmp = [...prev];
          tmp[passwordStep - 1] = null;
          return tmp;
        });
        setPasswordStep(prev => prev - 1);
      }
    } else if (!!val) {
      setIsMissMatching(false);
      setPasswordStrList(prev => {
        const tmp = [...prev];
        tmp[passwordStep] = val;
        return tmp;
      });
      setPasswordStep(prev => prev + 1);
    }
  }, [passwordStep, passwordStrList]);

  const checkPassword = useCallback(async () => {
    if (!passwordStepDone || loading) {
      return;
    }
    setLoading(true);
    setIsMissMatching(false);
    const password = passwordStrList.join("");
    const ret = await ComparePasswordToServer(password);
    if (ret?.data?.result) {
      dispatch({ type: AvatarConstant.SET_PASSWORD, value: password });
      await UpdateAvatarToUserConfirm();
      handlerClose();
    } else {
      setIsMissMatching(true);
      setPasswordStep(0);
      setPasswordStepDone(false);
      setPasswordStrList((new Array(4)).fill(null));
    }
    setLoading(false);
  }, [dispatch, handlerClose, UpdateAvatarToUserConfirm, passwordStrList, passwordStepDone, ComparePasswordToServer, loading]);

  return <>
    <Offcanvas
      show={show}
      onHide={handlerClose}
      placement="end"
      className="w-100 z-index-1049"
      backdropClassName="z-index-1048"
      style={{
        maxWidth: 600,
        borderLeft: "none",
        borderRight: "none",
      }}
    >
      <Offcanvas.Header className="gap-3 bg-primary" style={{ boxSizing: "content-box" }}>
        <Offcanvas.Title>
          <MLeftButton size={40} className="text-white" onClick={handlerClose} />
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="d-flex flex-column user-select-none p-0">
        <div className="d-flex flex-fill flex-column justify-content-center align-items-center bg-primary text-white text-center">
          <div className="fs-1 fw-bolder px-4">
            {t("Enter Password")}
          </div>
          {!isMissMatching && <>
            <div className="mt-2 px-2 fs-7" style={{ height: 50 }}>
              {t("Please enter a four-digit password.")}
            </div>
          </>}
          {isMissMatching && <>
            <div className="mt-2 px-2 fs-7 text-danger-600" style={{ height: 50 }}>
              {t("That's the wrong password! Please re-enter.")}
            </div>
          </>}
          <div className="pt-2 pb-5 d-flex align-items-center justify-content-around px-5 w-100">
            {!isMissMatching && <>
              {passwordStrList.map((x, idx) => <div key={idx}>
                {idx === passwordStep && <><MDotActive /></>}
                {idx !== passwordStep && <>
                  {!x && <><MDotEmpty /></>}
                  {x && <><MDotFill /></>}
                </>}
              </div>)}
            </>}
            {isMissMatching && <>
              {passwordStrList.map((x, idx) => <div key={idx}>
                <MDotWrong />
              </div>)}
            </>}
          </div>
        </div>
        <Container className="mt-2">
          <Row>
            {NumberPanel.map((x, idx) => <Col
              xs={4}
              key={x.value || `key-${idx}`}
              className="d-flex align-items-center justify-content-center g-4 fw-bolder fs-3 cursor-pointer"
              onClick={() => setPassword(x.value)}
            >
              <div className={x?.isBack && passwordStep === 0 ? "text-gray-300" : ""}>
                {!x?.isComponent && <>
                  {x.label}
                </>}
                {x?.isComponent && <>
                  {React.createElement(x.label, {
                    className: passwordStep === 0 ? "text-gray-400" : "text-black"
                  })}
                </>}
              </div>
            </Col>)}
          </Row>
        </Container>
        <div className="px-4 pb-4 pt-4">
          <MButton
            color={passwordStepDone ? "primary" : "gray-300"}
            circle={true}
            height={56}
            disabled={!passwordStepDone || loading}
            css="w-100 text-white"
            onClick={checkPassword}
          >
            {t("Next")}
          </MButton>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  </>
};

const mapState = (state) => {
  const show = state.AppReducer.show.avatarPassword;
  return { show };
};

const mapDispatch = dispatch => ({
  ComparePasswordToServer: (password) => dispatch(AvatarAction.ComparePasswordToServer(password)),
  UpdateAvatarToUserConfirm: () => dispatch(AvatarAction.UpdateAvatarToUserConfirm()),
});

export default connect(mapState, mapDispatch)(AvatarPasswordSidebar);
